import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { g, p, pu, pa, del, eur } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { MenuImport } from "@/components/MenuImport";
import { AggiunteReparto } from "@/components/AggiunteReparto";
import { PizzaInventata } from "@/components/PizzaInventata";

const ALLERGENS = ["glutine", "latte", "uova", "pesce", "crostacei", "frutta a guscio", "soia", "solfiti"];
const empty = {
  name: "", category: "Pizze", price: "", description: "", department: "Cucina", course: 2,
  allergens: [], tags: [], surcharges: [], track_qty: false, qty_total: 0, sold_out: false, tip: "",
};

export default function MenuPage() {
  const { departments, settings, refresh } = useApp();
  const [menu, setMenu] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => g("/menu").then(setMenu), []);
  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!form.name || !form.price) return toast.error("Nome e prezzo obbligatori");
    const body = { ...form, price: Number(form.price), course: Number(form.course), qty_total: Number(form.qty_total) || 0 };
    if (editId) await pu(`/menu/${editId}`, body);
    else await p("/menu", body);
    toast.success(editId ? "Piatto aggiornato" : "Piatto aggiunto");
    setOpen(false);
    setForm(empty);
    setEditId(null);
    load();
  };

  const toggleAll = (a) =>
    setForm((f) => ({ ...f, allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a] }));
  const toggleTag = (t) =>
    setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  const cats = [...new Set(menu.map((m) => m.category))];
  // La pizza da comporre nasce a zero per scelta: il prezzo si fa con base e ingredienti
  const senzaPrezzo = menu.filter((m) => !(m.price > 0) && !(m.tags || []).includes("componi")).length;

  return (
    <div className="fade-up space-y-6" data-testid="menu-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Pannello titolare</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Menu</h1>
          <p className="mt-1 text-xs text-slate-500">Solo il titolare può modificare menu e prezzi.</p>
        </div>
        <div className="flex flex-wrap gap-2">
        <MenuImport onDone={load} />
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button className="h-11" data-testid="add-menu-item-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Nuovo piatto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="menu-dialog">
            <DialogHeader>
              <DialogTitle>{editId ? "Modifica piatto" : "Nuovo piatto"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nome del piatto</Label>
                <Input className="mt-1" placeholder="Es. Tagliatelle al ragù" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="menu-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Categoria</Label>
                  <Input className="mt-1" placeholder="Es. Primi" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="menu-category-input" />
                </div>
                <div>
                  <Label className="text-xs">Prezzo in euro</Label>
                  <Input className="mt-1" type="number" step="0.5" placeholder="0,00" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="menu-price-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Descrizione per il cliente</Label>
                <Textarea className="mt-1" placeholder="Ingredienti e provenienza, come li racconti al tavolo" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="menu-desc-input" />
              </div>
              <div>
                <Label className="text-xs">Reparto di stampa</Label>
                <div className="mt-1.5 flex gap-2">
                  {departments.map((d) => (
                    <Button key={d} type="button" size="sm" variant={form.department === d ? "default" : "outline"} onClick={() => setForm({ ...form, department: d })} data-testid={`menu-dept-${d}`}>
                      {d}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Giro consigliato</Label>
                <p className="text-[11px] text-slate-500">
                  Con quale giro entra nella comanda: la sala può sempre spostarlo.
                </p>
                <div className="mt-1.5 flex gap-2">
                  {[1, 2, 3, 4, 5].map((c) => (
                    <Button key={c} type="button" size="sm" variant={Number(form.course) === c ? "default" : "outline"} onClick={() => setForm({ ...form, course: c })} data-testid={`menu-course-${c}`}>
                      {c}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Allergeni</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {ALLERGENS.map((a) => (
                    <button
                      key={a}
                      type="button"
                      data-testid={`menu-allergen-${a.replace(/\s+/g, "-")}`}
                      onClick={() => toggleAll(a)}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${form.allergens.includes(a) ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-600"}`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Tag</Label>
                <div className="mt-1.5 flex gap-1.5">
                  {["Vegetariano", "Vegano"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`menu-tag-${t.toLowerCase()}`}
                      onClick={() => toggleTag(t)}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${form.tags.includes(t) ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <Input placeholder="Consiglio del titolare (opzionale)" value={form.tip} onChange={(e) => setForm({ ...form, tip: e.target.value })} data-testid="menu-tip-input" />
              <div className="flex items-center justify-between rounded-md border p-3">
                <Label className="text-sm">Disponibilità limitata (dolci/pesce)</Label>
                <Switch checked={form.track_qty} onCheckedChange={(v) => setForm({ ...form, track_qty: v })} data-testid="menu-track-switch" />
              </div>
              {form.track_qty && (
                <Input type="number" placeholder="Quantità del giorno" value={form.qty_total} onChange={(e) => setForm({ ...form, qty_total: e.target.value })} data-testid="menu-qty-input" />
              )}
              <Button className="h-11 w-full" onClick={submit} data-testid="save-menu-item-btn">
                Salva
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <AggiunteReparto departments={departments} />
      {menu.some((m) => (m.tags || []).includes("componi")) && <PizzaInventata />}

      {settings && !settings.prices_checked && menu.length > 0 && (
        <Card className="flex flex-wrap items-center justify-between gap-3 border-2 border-amber-300 bg-amber-50 p-4" data-testid="prezzi-da-controllare">
          <div>
            <p className="text-sm font-bold text-amber-900">
              {senzaPrezzo > 0
                ? `${senzaPrezzo} ${senzaPrezzo === 1 ? "piatto" : "piatti"} senza prezzo: mettilo prima di iniziare`
                : "Listino di partenza: controlla i prezzi"}
            </p>
            <p className="mt-0.5 text-xs text-amber-900/80">
              I piatti, gli ingredienti e le aggiunte sono già pronti. I prezzi no, perché cambiano da
              locale a locale: mettili tu, oppure carica il tuo listino (PDF o Excel) e li compila da solo.
            </p>
          </div>
          <Button
            size="sm"
            className="bg-amber-900 hover:bg-amber-800"
            data-testid="prezzi-confermati"
            onClick={async () => {
              try {
                await pu("/settings", { ...settings, prices_checked: true });
                toast.success("Prezzi confermati");
                refresh?.();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Non salvato");
              }
            }}
          >
            Prezzi controllati
          </Button>
        </Card>
      )}


      {cats.map((c) => (
        <section key={c} className="space-y-3">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{c}</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {menu.filter((m) => m.category === c).map((m) => (
              <Card key={m.id} className={`p-5 ${m.sold_out ? "opacity-60" : ""}`} data-testid={`menu-row-${m.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold leading-tight">{m.name}</p>
                    {m.description && (
                      <p className="mt-0.5 text-[12px] leading-snug text-slate-600" data-testid={`ingredienti-menu-${m.id}`}>
                        {m.description}
                      </p>
                    )}
                    <p className="text-[11px] text-slate-500">{m.department} · giro {m.course}</p>
                    {m.allergens.length > 0 && <p className="mt-1 text-[11px] text-rose-600">{m.allergens.join(", ")}</p>}
                    {m.tags.length > 0 && <p className="text-[11px] text-emerald-700">{m.tags.join(" · ")}</p>}
                  </div>
                  {m.price > 0 ? (
                    <span className="font-num shrink-0 text-lg font-bold">{eur(m.price)}</span>
                  ) : (
                    <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-900"
                          data-testid={`senza-prezzo-${m.id}`}>
                      prezzo da mettere
                    </span>
                  )}
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`edit-menu-${m.id}`}
                    onClick={() => {
                      setForm({ ...m, price: String(m.price) });
                      setEditId(m.id);
                      setOpen(true);
                    }}
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Modifica
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-rose-600"
                    data-testid={`delete-menu-${m.id}`}
                    onClick={async () => {
                      await del(`/menu/${m.id}`);
                      toast.success("Piatto eliminato");
                      load();
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <div className="ml-auto flex items-center gap-2">
                    {(m.variants || []).length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-amber-700"
                        data-testid={`togli-scelte-${m.id}`}
                        onClick={async () => {
                          await pa(`/menu/${m.id}/scelte`, {});
                          toast.success("Ora si ordina con un tocco solo");
                          load();
                        }}
                      >
                        Togli la scelta
                      </Button>
                    )}
                    <span className="text-[11px] text-slate-500">Finito</span>
                    <Switch
                      checked={m.sold_out}
                      data-testid={`soldout-${m.id}`}
                      onCheckedChange={async (v) => {
                        await pa(`/menu/${m.id}/soldout`, { sold_out: v });
                        load();
                      }}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
